import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Film, Play, Clock, Calendar, Edit2, X, RefreshCw } from 'lucide-react'
import { videos } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { useVideoPlayerStore } from '../stores/videoPlayer'
import VideoMenu from '../components/VideoMenu'
import type { Video } from '../types'

function formatDuration(ms: number | null): string {
  if (!ms) return ''
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

export default function Movie() {
  const { id } = useParams<{ id: string }>()
  const token = useAuthStore((state) => state.token)
  const { playVideo, currentVideo, isPlaying } = useVideoPlayerStore()
  const queryClient = useQueryClient()
  const [showEditModal, setShowEditModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [posterUrl, setPosterUrl] = useState('')
  const [overview, setOverview] = useState('')

  const { data: movie, isLoading } = useQuery<Video>({
    queryKey: ['movie', id],
    queryFn: () => videos.get(id!),
    enabled: !!id,
  })

  const { data: watchedData } = useQuery({
    queryKey: ['video-watched', id],
    queryFn: () => videos.getWatchedStatus(id!),
    enabled: !!id,
  })

  const isWatched = watchedData?.watched || false

  // Initialize form when movie loads or modal opens
  useEffect(() => {
    if (movie && showEditModal) {
      setPosterUrl(movie.thumbnail_path || '')
      setOverview(movie.overview || '')
    }
  }, [movie, showEditModal])

  const handleSaveMetadata = async () => {
    if (!id || isSaving) return
    setIsSaving(true)
    try {
      await videos.updateMetadata(id, overview || undefined, posterUrl || undefined)
      queryClient.invalidateQueries({ queryKey: ['movie', id] })
      queryClient.invalidateQueries({ queryKey: ['movies'] })
      setShowEditModal(false)
    } catch (error) {
      console.error('Failed to save metadata:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRefreshMetadata = async () => {
    if (!id || isRefreshing) return
    setIsRefreshing(true)
    try {
      await videos.refreshMetadata(id)
      queryClient.invalidateQueries({ queryKey: ['movie', id] })
      queryClient.invalidateQueries({ queryKey: ['movies'] })
    } catch (error) {
      console.error('Failed to refresh metadata:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const getStreamUrl = () => {
    if (!id) return ''
    if (import.meta.env.DEV) {
      const host = window.location.hostname
      return `http://${host}:4322/api/stream/video/${id}/transcode?token=${token}`
    }
    return `/api/stream/video/${id}/transcode?token=${token}`
  }

  const handlePlay = () => {
    if (!movie) return

    playVideo({
      id: movie.id,
      title: movie.title,
      thumbnailPath: undefined,
      durationMs: movie.duration_ms ?? undefined,
      streamUrl: getStreamUrl(),
      mediaType: 'movie',
    })
  }

  const handleWatchedChange = (_watched: boolean) => {
    // The VideoMenu component handles this internally
  }

  const isCurrentlyPlaying = currentVideo?.id === id && isPlaying

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!movie) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Movie not found</div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      {/* Back button */}
      <Link
        to="/movies"
        className="inline-flex items-center gap-2 text-dark-400 hover:text-white mb-4 sm:mb-6 text-sm sm:text-base"
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        Back to Movies
      </Link>

      {/* Movie Header with Poster */}
      <div className="flex flex-col sm:flex-row gap-6 mb-6">
        {/* Poster */}
        <div
          onClick={handlePlay}
          className="flex-shrink-0 w-full sm:w-48 aspect-[2/3] bg-dark-800 rounded-xl flex items-center justify-center cursor-pointer group relative overflow-hidden"
        >
          {movie.thumbnail_path ? (
            <img
              src={movie.thumbnail_path}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <Film size={64} className="text-dark-500" />
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
              <Play size={32} className="text-white ml-1" />
            </div>
          </div>
          {isCurrentlyPlaying && (
            <div className="absolute bottom-2 left-2 bg-primary-500 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Playing
            </div>
          )}
        </div>

        {/* Movie details */}
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">{movie.title}</h1>

          {/* Metadata badges */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-dark-400 mb-4">
            {movie.duration_ms && (
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {formatDuration(movie.duration_ms)}
              </span>
            )}
            {movie.year && (
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {movie.year}
              </span>
            )}
            {movie.resolution && (
              <span className="px-2 py-1 bg-dark-700 rounded text-xs">{movie.resolution}</span>
            )}
            {movie.codec && (
              <span className="px-2 py-1 bg-dark-700 rounded text-xs uppercase">{movie.codec}</span>
            )}
            {!isWatched && (
              <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">New</span>
            )}
          </div>

          {/* Overview / Description */}
          {movie.overview && (
            <p className="text-dark-300 text-sm leading-relaxed mb-4">
              {movie.overview}
            </p>
          )}

          {/* Play button */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePlay}
              className="btn btn-primary flex items-center gap-2"
            >
              <Play size={20} />
              {isCurrentlyPlaying ? 'Playing' : 'Play Movie'}
            </button>
            <button
              onClick={handleRefreshMetadata}
              disabled={isRefreshing}
              className="btn btn-secondary flex items-center gap-2"
              title="Fetch metadata from iTunes"
            >
              <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="btn btn-secondary flex items-center gap-2"
              title="Edit poster and description manually"
            >
              <Edit2 size={18} />
              Edit
            </button>
            <VideoMenu
              videoId={movie.id}
              isWatched={isWatched}
              onWatchedChange={handleWatchedChange}
            />
          </div>
        </div>
      </div>

      {/* File info card */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-3">File Information</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {movie.resolution && (
            <div>
              <span className="text-dark-500">Resolution</span>
              <p className="text-dark-200">{movie.resolution}</p>
            </div>
          )}
          {movie.codec && (
            <div>
              <span className="text-dark-500">Codec</span>
              <p className="text-dark-200 uppercase">{movie.codec}</p>
            </div>
          )}
          {movie.container && (
            <div>
              <span className="text-dark-500">Container</span>
              <p className="text-dark-200 uppercase">{movie.container}</p>
            </div>
          )}
          {movie.duration_ms && (
            <div>
              <span className="text-dark-500">Duration</span>
              <p className="text-dark-200">{formatDuration(movie.duration_ms)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Metadata Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-dark-700">
              <h2 className="text-lg font-semibold">Edit Metadata</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-dark-700 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-dark-400 mb-1">Poster URL</label>
                <input
                  type="url"
                  value={posterUrl}
                  onChange={(e) => setPosterUrl(e.target.value)}
                  placeholder="https://image.tmdb.org/t/p/w500/..."
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <p className="text-xs text-dark-500 mt-1">
                  Find poster on TMDB/IMDb, right-click image → Copy image address
                </p>
              </div>
              {posterUrl && (
                <div>
                  <label className="block text-sm text-dark-400 mb-1">Preview</label>
                  <img
                    src={posterUrl}
                    alt="Poster preview"
                    className="w-32 h-48 object-cover rounded-lg bg-dark-700"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm text-dark-400 mb-1">Description</label>
                <textarea
                  value={overview}
                  onChange={(e) => setOverview(e.target.value)}
                  placeholder="Movie description..."
                  rows={4}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-primary-500 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-dark-700">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMetadata}
                disabled={isSaving}
                className="btn btn-primary"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
